import { useEffect } from 'react';
import { useLocation } from 'react-router';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { SxProps } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';

import {
    IOfferEventListItemFragment,
    IOfferEventType,
    OfferEventsDocument,
} from 'graphql/types';
import { Loader } from 'common/components/Loader/Loader';
import { ApolloError } from 'common/components/ApolloError';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import { IGlobalDrawerType } from 'common/types';
import { List } from 'common/components/List';

import { OfferEventListItem } from '../OfferEventListItem';

const EMPTY_EVENTS: IOfferEventListItemFragment[] = [];

interface IProps {
    offerId: string;
    className?: string;
    fromDate?: string;
    noPadding?: boolean;
    setFirstOfferEventType?: (offerEventType: IOfferEventType) => void;
    sx?: SxProps;
}

export const OfferEventList = ({
    offerId,
    className,
    fromDate,
    noPadding,
    setFirstOfferEventType,
    sx,
}: IProps) => {
    const location = useLocation();
    const [translate] = useTranslation();
    const { openGlobalDrawer } = useGlobalDrawer();
    const { data, loading, error } = useQuery(OfferEventsDocument, {
        variables: { offerId, fromDate },
    });

    const offerEvents = data?.offerEvents ?? EMPTY_EVENTS;

    useEffect(() => {
        if (loading || !offerEvents.length) return;

        setFirstOfferEventType?.(offerEvents[0].type);
    }, [loading, offerEvents, setFirstOfferEventType]);

    const handleClick = (offerEvent: IOfferEventListItemFragment) => {
        openGlobalDrawer(
            {
                type: IGlobalDrawerType.OfferEvent,
                itemId: offerEvent.id,
            },
            location
        );
    };

    return (
        <>
            {error && <ApolloError error={error} />}
            {loading ? (
                <Loader />
            ) : (
                <>
                    {offerEvents.length ? (
                        <List
                            noBorder
                            className={className}
                            noPadding={noPadding}
                            sx={sx}
                        >
                            {offerEvents.map((item) => {
                                const hasDates =
                                    item.type === IOfferEventType.Webinar ||
                                    item.type === IOfferEventType.Meeting;
                                const datesExpired =
                                    hasDates &&
                                    !!item.dates.length &&
                                    item.dates.every(
                                        (date) =>
                                            new Date(
                                                date.endDate ?? date.startDate
                                            ) < new Date()
                                    );

                                return (
                                    <OfferEventListItem
                                        disabled={datesExpired}
                                        key={item.id}
                                        {...item}
                                        onClick={() => handleClick(item)}
                                    />
                                );
                            })}
                        </List>
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                mt: 8,
                            }}
                        >
                            <Typography>
                                {translate('noResults.offerEvents')}
                            </Typography>
                        </Box>
                    )}
                </>
            )}
        </>
    );
};
